import React, { useEffect, useRef } from 'react';
import {
    Text,
    makeStyles
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { QuickSaveForm } from './components/QuickSaveForm';
import { PageHeader } from './components/PageHeader';
import { useQuickSaveState } from './hooks/useQuickSaveState';
import { useFormState } from './hooks/useFormState';
import { getDocumentDetailComp } from '../../common/components/home/HomeComponents';
import type {
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult
} from '../../services/api/dataServiceOutlookQuickSave';
import { navigateSafely } from '../../utils/navigation';

interface PreservedState {
    selectedArchive?: InsertableArchiveResult;
    selectedCategory?: EmailDocumentCategoryResult;
    selectedCase?: CaseResult;
    selectedRecordType?: RecordTypeResult;
    selectedCopyTo?: CarbonCopyContactResult[];
    title?: string;
    isRestricted?: boolean;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '15px',
    },
});

const QuickSaveContent: React.FC = () => {
    const { t } = useTranslation();
    const styles = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const preservedState = location.state as PreservedState | undefined;

    const state = useQuickSaveState(preservedState);
    const formState = useFormState({
        selectedArchive: state.selectedArchive,
        selectedCategory: state.selectedCategory,
        selectedCase: state.selectedCase,
        selectedRecordType: state.selectedRecordType,
        selectedCopyTo: state.selectedCopyTo,
        title: state.title,
        caseConnection: state.caseConnection,
        showRecordTypes: state.showRecordTypes
    });

    // Use ref to track if we've already restored the state
    const hasRestoredRef = useRef(false);

    // Restore isRestricted from preserved state
    useEffect(() => {
        if (!hasRestoredRef.current && preservedState?.isRestricted !== undefined) {
            formState.setIsRestricted(preservedState.isRestricted);
            hasRestoredRef.current = true;
        }
    }, [preservedState?.isRestricted, formState]);

    const setIsRestricted = formState.setIsRestricted;
    useEffect(() => {
        if (state.caseValidation) {
            setIsRestricted(Boolean(state.caseValidation.AccessProperties?.IsRestricted));
        }
    }, [state.caseValidation, setIsRestricted]);

    useEffect(() => {
        const onItemChanged = () => navigateSafely(navigate, '/archivehome');
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, onItemChanged);

        return () => {
            Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
        };
    }, [navigate]);

    // If form submission was successful and we have document details, show them
    if (formState.submitSuccess && formState.documentDetails) {
        return (
            <div className={styles.container}>
                {getDocumentDetailComp(formState.documentDetails)}
            </div>
        );
    }

    // Show the form if not yet successfully submitted
    return (
        <div className={styles.container}>
            <PageHeader
                title={t("Save email")}
                description={t("Save as a new document in 360°")}
            />

            <QuickSaveForm state={state} formState={formState} />

            {!state.isLoadingArchives && state.archives.length === 0 && !state.error && (
                <Text>{t("No archives available")}</Text>
            )}
        </div>
    );
};

const QuickSave: React.FC = () => {
    const location = useLocation();

    return <QuickSaveContent key={location.key} />;
};

export default QuickSave;
